/**
 * CZNA-1106 — turn the file the cataloguer picked into what the upload endpoint
 * receives: a name no other upload can collide with, and a body small enough to
 * pass the gateway.
 *
 * Why the name: the backend stores an upload under its base name in one shared
 * temp dir and overwrites on collision. Two cataloguers importing "export.xml"
 * at once would each preview — and confirm — the other's file. A random prefix
 * keeps the name the user recognises and makes it unique.
 *
 * Why gzip: the FOLIO sidecar refuses a request body over 10 MiB, and a MARCXML
 * export of the 10 000 records the export allows is well over that. The backend
 * already reads `.xml.gz`, and MARCXML compresses roughly tenfold, so compressing
 * in the browser moves the ceiling without touching the platform.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/** The sidecar's request-body cap; see the header comment. */
export var UPLOAD_LIMIT_BYTES = 10 * 1024 * 1024;
export var ACCEPTED_IMPORT_EXTENSIONS = ['.xml', '.xml.gz'];
export var isAcceptedImportFile = function (fileName) {
    var lower = fileName.toLowerCase();
    return ACCEPTED_IMPORT_EXTENSIONS.some(function (ext) { return lower.endsWith(ext); });
};
var randomPrefix = function () {
    var cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
        return cryptoApi.randomUUID().slice(0, 8);
    }
    return Math.random().toString(36).slice(2, 10);
};
/**
 * Keeps only characters the backend's sanitizer and a shell both leave alone,
 * so the batch name on the Importy screen reads the same as the file picked.
 */
var safeBaseName = function (fileName) { return fileName
    .replace(/^.*[\\/]/, '')
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    // The backend's extension check is case-sensitive; "EXPORT.XML" would be refused.
    .replace(/\.xml(\.gz)?$/i, function (ext) { return ext.toLowerCase(); }); };
function defaultCompress() {
    var Ctor = globalThis
        .CompressionStream;
    if (!Ctor || typeof Blob === 'undefined' || typeof Response === 'undefined')
        return null;
    return function (input) { return new Response(input.stream().pipeThrough(new Ctor('gzip'))).blob(); };
}
/**
 * @param compress injectable for tests; defaults to the browser's
 *   `CompressionStream`, and is `null` where the browser has none (then the
 *   file goes up as picked, and the 10 MiB cap applies to it unchanged).
 */
export var prepareImportFile = function (file_1) {
    var args_1 = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args_1[_i - 1] = arguments[_i];
    }
    return __awaiter(void 0, __spreadArray([file_1], args_1, true), void 0, function (file, compress) {
        var base;
        var _a;
        if (compress === void 0) { compress = defaultCompress(); }
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    base = "".concat(randomPrefix(), "-").concat(safeBaseName(file.name));
                    if (!(base.endsWith('.xml') && compress)) return [3 /*break*/, 2];
                    _a = {};
                    return [4 /*yield*/, compress(file)];
                case 1: return [2 /*return*/, (_a.body = _b.sent(), _a.name = "".concat(base, ".gz"), _a)];
                case 2: return [2 /*return*/, { body: file, name: base }];
            }
        });
    });
};
